import type { LoadTrackingCallbacks } from '@ctw/shared/context/load-tracking-context';
import type {
  TelemetryContext,
  useTelemetry,
} from '@ctw/shared/context/telemetry/telemetry-boundary';
import type { TimerMetricIdentifier } from '@ctw/shared/context/telemetry/timer-metrics';
import { useCallback, useEffect, useMemo, useRef } from 'react';

export type OverlayLoadTrigger = 'user' | 'deep-link' | 'reload';

type OverlayLoadStatus = 'complete' | 'abandoned' | 'superseded';

/* How long the pending load count has to stay at zero before the overlay counts as loaded. It
 * bridges the gap between one load resolving and the load it unlocks registering, and between the
 * overlay opening and its content mounting its first load. The window delays emission but is
 * never part of the reported duration.
 */
const SETTLE_QUIET_WINDOW_MS = 250;

interface OverlayLoadTracking {
  startedAt: number;
  /* When the content last became fully settled, or `null` while loads are outstanding. Starts at
   * `startedAt` because an overlay whose content never loads anything was ready the moment it
   * opened.
   */
  settledAt: number | null;
  trigger: OverlayLoadTrigger;
  telemetry: ReturnType<typeof useTelemetry>;
  context: TelemetryContext;
  hadPendingLoads: boolean;
  sawLoadError: boolean;
}

export interface BeginOverlayLoadTrackingArgs {
  startedAt: number;
  trigger: OverlayLoadTrigger;
  telemetry: ReturnType<typeof useTelemetry>;
  context: TelemetryContext;
}

export interface OverlayLoadMetric {
  /* Starts the clock for a newly requested open, resolving any open still in flight as
   * `superseded`.
   */
  beginTracking: (args: BeginOverlayLoadTrackingArgs) => void;
  /* Extends the "nothing ever loaded" grace window across the open transition. */
  handleAfterOpen: () => void;
  /* Resolves the open the user just closed as `abandoned`. */
  handleClose: () => void;
  loadTrackingProps: LoadTrackingCallbacks;
}

/* Measures how long an overlay takes to finish loading its data, from the moment its open is
 * requested until every load registered with its `LoadTrackingProvider` has resolved. Emits
 * exactly one timer metric per open — see the `TIMER_DRAWER_LOAD_TIME` doc comment for the
 * contract this implements.
 *
 * All of the per-open bookkeeping lives in refs: none of it affects what the overlay renders, and
 * every drawer and modal in the app runs through here, so it must not cost a re-render per
 * resolved query.
 */
export const useOverlayLoadMetric = (timerMetricId: TimerMetricIdentifier): OverlayLoadMetric => {
  const trackingRef = useRef<OverlayLoadTracking | null>(null);
  const settleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSettleTimeout = useCallback(() => {
    if (settleTimeoutRef.current) {
      clearTimeout(settleTimeoutRef.current);
      settleTimeoutRef.current = null;
    }
  }, []);

  const emit = useCallback(
    (loadStatus: OverlayLoadStatus, endTime: number) => {
      const tracking = trackingRef.current;

      if (!tracking) {
        return;
      }

      clearSettleTimeout();
      trackingRef.current = null;

      tracking.telemetry.trackTimerMetric('loading', timerMetricId, tracking.startedAt, endTime, {
        ...tracking.context,
        loadStatus,
        loadTrigger: tracking.trigger,
        hadPendingLoads: tracking.hadPendingLoads,
        sawLoadError: tracking.sawLoadError,
      });
    },
    [clearSettleTimeout, timerMetricId],
  );

  /* Ends the current open. Content that had already settled counts as `complete` however the open
   * ended — the quiet window is a debounce, not a deadline the user has to beat.
   */
  const resolveTracking = useCallback(
    (unsettledStatus: OverlayLoadStatus) => {
      const settledAt = trackingRef.current?.settledAt;

      if (typeof settledAt === 'number') {
        emit('complete', settledAt);
      } else {
        emit(unsettledStatus, Date.now());
      }
    },
    [emit],
  );

  const armSettleTimeout = useCallback(() => {
    clearSettleTimeout();

    settleTimeoutRef.current = setTimeout(() => {
      settleTimeoutRef.current = null;
      const settledAt = trackingRef.current?.settledAt;

      if (typeof settledAt === 'number') {
        emit('complete', settledAt);
      }
    }, SETTLE_QUIET_WINDOW_MS);
  }, [clearSettleTimeout, emit]);

  // Nothing is emitted for an overlay whose provider unmounts mid-load; the page is going away
  // with it, and a timer left armed would fire against a torn-down tree.
  useEffect(() => clearSettleTimeout, [clearSettleTimeout]);

  return useMemo(
    () => ({
      beginTracking: ({ startedAt, trigger, telemetry, context }) => {
        resolveTracking('superseded');

        trackingRef.current = {
          startedAt,
          settledAt: startedAt,
          trigger,
          telemetry,
          context,
          hadPendingLoads: false,
          sawLoadError: false,
        };

        armSettleTimeout();
      },
      handleAfterOpen: () => {
        if (trackingRef.current && !trackingRef.current.hadPendingLoads) {
          armSettleTimeout();
        }
      },
      handleClose: () => resolveTracking('abandoned'),
      loadTrackingProps: {
        onFirstPending: () => {
          if (trackingRef.current) {
            trackingRef.current.hadPendingLoads = true;
            trackingRef.current.settledAt = null;
          }

          clearSettleTimeout();
        },
        onSettle: ({ settledAt, sawError }) => {
          if (!trackingRef.current) {
            return;
          }

          trackingRef.current.settledAt = settledAt;
          trackingRef.current.sawLoadError = trackingRef.current.sawLoadError || sawError;

          armSettleTimeout();
        },
      },
    }),
    [armSettleTimeout, clearSettleTimeout, resolveTracking],
  );
};
